<template>
    <div class="shipment-open-report-filters">
        <div class="shipment-open-report-filters__field">
            <v-text-field
                v-model="localDateFrom"
                dense
                outlined
                type="date"
                :label="$t('pages.report.from')"
                hide-details
                @input="onDateFromChange"
            ></v-text-field>
        </div>
        <div class="shipment-open-report-filters__field">
            <v-text-field
                v-model="localDateTo"
                dense
                outlined
                type="date"
                :label="$t('pages.report.to')"
                hide-details
                :min="localDateFrom"
                @input="onDateToChange"
            ></v-text-field>
        </div>
        <div class="shipment-open-report-filters__field shipment-open-report-filters__field--managers">
            <v-autocomplete
                v-model="localManagerIds"
                :items="managerItems"
                :loading="isLoadingManagers"
                dense
                multiple
                chips
                clearable
                :label="$t('pages.report.openShipmentReport.manager')"
                hide-details
                hide-no-data
                :placeholder="$t('pages.report.any')"
                class="compact-chip-autocomplete"
                @change="onManagersChange"
            >
                <template #selection="{ item, index }">
                    <v-chip
                        v-if="index === 0"
                        small
                        class="mr-1"
                        close
                        @click:close="removeChip(item)"
                    >
                        {{ item.text }}
                    </v-chip>

                    <span
                        v-if="index === 1 && localManagerIds.length > 1"
                        class="status-chip-more"
                    >
                        +{{ localManagerIds.length - 1 }}
                    </span>
                </template>
            </v-autocomplete>
        </div>
        <v-btn
            class="shipment-open-report-filters__btn"
            dark
            color="#967adc"
            :loading="isLoading"
            @click="onReload"
        >
            {{ $t('pages.report.reload') }}
        </v-btn>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { actionTypes, getterTypes } from '@/store/modules/shipments'

export default {
    name: 'ShipmentOpenReportFilters',
    props: {
        dateFrom: {
            type: String,
            default: '',
        },
        dateTo: {
            type: String,
            default: '',
        },
        managerIds: {
            type: Array,
            default: () => [],
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['filters-change', 'reload'],
    data() {
        return {
            localDateFrom: this.dateFrom,
            localDateTo: this.dateTo,
            localManagerIds: [...this.managerIds],
            debounceTimer: null,
        }
    },
    computed: {
        ...mapGetters('shipments', {
            usersWithShipments: getterTypes.getUsersWithShipments,
            isLoadingUsersWithShipments: getterTypes.isLoadingUsersWithShipments,
        }),
        isLoadingManagers() {
            return this.isLoadingUsersWithShipments
        },
        managerItems() {
            return this.usersWithShipments.map((user) => ({
                text: user.nickname || user.nickName || `User ${user.id}`,
                value: user.id,
            }))
        },
    },
    watch: {
        dateFrom(newVal) {
            this.localDateFrom = newVal
        },
        dateTo(newVal) {
            this.localDateTo = newVal
        },
        managerIds(newVal) {
            this.localManagerIds = [...newVal]
        },
    },
    mounted() {
        this.loadManagers()
    },
    methods: {
        ...mapActions('shipments', {
            fetchUsersWithShipments: actionTypes.fetchUsersWithShipments,
        }),
        loadManagers() {
            if (this.usersWithShipments.length === 0) {
                this.fetchUsersWithShipments()
            }
        },
        debounceFilterChange(callback) {
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer)
            }
            this.debounceTimer = setTimeout(() => {
                callback()
            }, 500)
        },
        onDateFromChange() {
            this.debounceFilterChange(() => {
                this.$emit('filters-change', {
                    dateFrom: this.localDateFrom,
                    dateTo: this.localDateTo,
                    managerIds: this.localManagerIds,
                })
            })
        },
        onDateToChange() {
            if (
                this.localDateTo &&
                this.localDateFrom &&
                this.localDateTo < this.localDateFrom
            ) {
                this.localDateTo = this.localDateFrom
            }
            this.debounceFilterChange(() => {
                this.$emit('filters-change', {
                    dateFrom: this.localDateFrom,
                    dateTo: this.localDateTo,
                    managerIds: this.localManagerIds,
                })
            })
        },
        onManagersChange() {
            this.$emit('filters-change', {
                dateFrom: this.localDateFrom,
                dateTo: this.localDateTo,
                managerIds: this.localManagerIds,
            })
        },
        onReload() {
            this.$emit('reload')
        },
        removeChip(item) {
            this.localManagerIds = this.localManagerIds.filter(
                (id) => id !== item.value
            )
            this.onManagersChange()
        },
    },
}
</script>

<style lang="scss" scoped>
.shipment-open-report-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 24px;
    align-items: flex-end;

    &__field {
        min-width: 200px;
        max-width: 250px;

        &--managers {
            max-width: 324px;
            min-width: 324px;
        }
    }

    &__btn {
        max-width: 130px;
        height: 40px;
    }
}

.compact-chip-autocomplete {
    ::v-deep .v-select__slot {
        height: 26px !important;
        min-height: 26px !important;
        max-height: 26px !important;
    }

    ::v-deep .v-input__control {
        height: 26px !important;
        min-height: 26px !important;
        max-height: 26px !important;
    }

    ::v-deep .v-input__slot {
        height: 26px !important;
        min-height: 26px !important;
        max-height: 26px !important;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        display: flex;
        align-items: center;
    }

    ::v-deep .v-select__selections {
        display: flex;
        align-items: center;
        overflow: hidden;
        height: 26px !important;

        input {
            max-height: 26px !important;
        }
    }

    ::v-deep .v-chip {
        height: 24px !important;
        min-height: 24px !important;
        font-size: 12px;
        margin-bottom: 2px !important;
    }

    .status-chip-more {
        font-size: 13px;
        opacity: 0.7;
        margin: 0 4px 2px 0;
        line-height: 1;
        display: flex;
        align-items: center;
        height: 24px;
    }
}
</style>

